import config from './config'
import { simplifyHeaders } from './simple-request'

export interface RequestHeaders {
  [key: string]: string
}

export interface RequestParams {
  [key: string]: string | number | boolean | (string | number | boolean)[] | null | undefined
}

export interface RequestConfig {
  baseURL?: string
  headers?: RequestHeaders
  params?: RequestParams
  simpleHeaders?: boolean
}

export interface RequestDefaults {
  baseURL?: string
  headers?: RequestHeaders
}

export interface SeBarRequest {
  defaults: RequestDefaults
  get<TResponse = any>(url: string, config?: RequestConfig): Promise<SeBarApiResponse<TResponse>>
  delete<TResponse = any>(url: string, config?: RequestConfig): Promise<SeBarApiResponse<TResponse>>
  head<TResponse = any>(url: string, config?: RequestConfig): Promise<SeBarApiResponse<TResponse>>
  post<TResponse = any>(url: string, data?: any, config?: RequestConfig): Promise<SeBarApiResponse<TResponse>>
  put<TResponse = any>(url: string, data?: any, config?: RequestConfig): Promise<SeBarApiResponse<TResponse>>
  setRequestBaseUrl(baseUrl: string): this
}

export interface SeBarApiMetadata {
  _trace_id?: string
  _pagination?: any
}

export type SeBarApiResponse<TResponse> = TResponse & SeBarApiMetadata

const unpackPayload = (data): SeBarApiResponse<any> => {
  const result: SeBarApiResponse<any> = data && data.result
  if (!(result && result instanceof Object)) return data
  const meta = data.metadata || {}
  if (result && meta.pagination) {
    result._pagination = meta.pagination
  }
  if (meta.trace_id) {
    result._trace_id = meta.trace_id
  }
  return result
}

export interface SeBarRequestError extends Error {
  response?: {
    data: any
    status: number
    statusText: string
    headers: Headers
  }
  data?: any
}

const ABSOLUTE_URL_PATTERN = /^([a-z][a-z\d+\-.]*:)?\/\//i
const requestDefaults: RequestDefaults = {
  baseURL: config.urls.seApi,
  headers: {
    Accept: 'application/vnd.ngin-api.v2, application/json',
    'X-SPORTS-VERSION': 'v1',
  },
}

function appendParams(url: string, params?: RequestParams): string {
  if (!params) return url

  const queryParams = Object.keys(params).reduce((result, key) => {
    const value = params[key]
    if (value === null || value === undefined) return result

    if (Array.isArray(value)) {
      value.forEach(item => result.push(`${encodeURIComponent(key)}=${encodeURIComponent(item.toString())}`))
    } else {
      result.push(`${encodeURIComponent(key)}=${encodeURIComponent(value.toString())}`)
    }
    return result
  }, [])

  if (!queryParams.length) return url

  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}${queryParams.join('&')}`
}

function buildUrl(url: string, requestConfig: RequestConfig): string {
  const baseURL = requestConfig.baseURL === undefined ? requestDefaults.baseURL : requestConfig.baseURL
  const resolvedUrl = baseURL && !ABSOLUTE_URL_PATTERN.test(url) ?
    `${baseURL.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}` :
    url

  return appendParams(resolvedUrl, requestConfig.params)
}

function hasHeader(headers: RequestHeaders, headerName: string): boolean {
  const normalizedHeaderName = headerName.toLocaleLowerCase()
  return Object.keys(headers).some(key => key.toLocaleLowerCase() === normalizedHeaderName)
}

function isNativeBody(data: any): boolean {
  return (typeof FormData !== 'undefined' && data instanceof FormData) ||
    (typeof Blob !== 'undefined' && data instanceof Blob) ||
    (typeof URLSearchParams !== 'undefined' && data instanceof URLSearchParams)
}

function applyDefaultContentType(data: any, headers: RequestHeaders): void {
  if (data === undefined || data === null || typeof data === 'string' || isNativeBody(data)) return

  if (!hasHeader(headers, 'Content-Type')) {
    headers['Content-Type'] = 'application/json;charset=utf-8'
  }
}

function buildBody(data: any): BodyInit | undefined {
  if (data === undefined || data === null) return undefined
  if (typeof data === 'string' || isNativeBody(data)) return data

  return JSON.stringify(data)
}

function buildHeaders(method: string, requestConfig: RequestConfig, data: any): RequestHeaders {
  const headers = Object.assign({}, requestDefaults.headers, requestConfig.headers)
  applyDefaultContentType(data, headers)

  if (requestConfig.simpleHeaders) {
    return simplifyHeaders(method, headers)
  }

  return headers
}

function parseResponse(response: Response): Promise<any> {
  return response.text().then((text) => {
    if (!text) return undefined

    try {
      return JSON.parse(text)
    } catch (err) {
      return text
    }
  })
}

function responseError(response: Response, data: any): SeBarRequestError {
  const error = new Error(`Request failed with status code ${response.status}`) as SeBarRequestError
  error.response = {
    data,
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  }

  if (error.response.data) {
    error.data = error.response.data.error
  }

  return error
}

function makeRequest<TResponse>(
  method: string,
  url: string,
  data?: any,
  requestConfig: RequestConfig = {},
): Promise<SeBarApiResponse<TResponse>> {
  const headers = buildHeaders(method, requestConfig, data)
  const body = buildBody(data)

  return fetch(buildUrl(url, requestConfig), {
    method: method.toLocaleUpperCase(),
    headers,
    body,
    credentials: 'include',
  }).then(response => parseResponse(response)
    .then((responseData) => {
      if (response.ok) return unpackPayload(responseData)
      throw responseError(response, responseData)
    }))
}

const request: SeBarRequest = {
  defaults: requestDefaults,
  get<TResponse = any>(url: string, config?: RequestConfig): Promise<SeBarApiResponse<TResponse>> {
    return makeRequest<TResponse>('get', url, undefined, config)
  },
  delete<TResponse = any>(url: string, config?: RequestConfig): Promise<SeBarApiResponse<TResponse>> {
    return makeRequest<TResponse>('delete', url, undefined, config)
  },
  head<TResponse = any>(url: string, config?: RequestConfig): Promise<SeBarApiResponse<TResponse>> {
    return makeRequest<TResponse>('head', url, undefined, config)
  },
  post<TResponse = any>(url: string, data?: any, config?: RequestConfig): Promise<SeBarApiResponse<TResponse>> {
    return makeRequest<TResponse>('post', url, data, config)
  },
  put<TResponse = any>(url: string, data?: any, config?: RequestConfig): Promise<SeBarApiResponse<TResponse>> {
    return makeRequest<TResponse>('put', url, data, config)
  },
  setRequestBaseUrl(baseUrl: string): SeBarRequest {
    this.defaults.baseURL = baseUrl
    return this
  },
}

export { request }
export default request
